import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

export default function AdminQuotes() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const { data } = await api.get("/admin/quotes", { params });
      setRows(data);
    } catch (e) { toast.error(errMsg(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex items-end justify-between gap-3">
        <div>
          <span className="kbd-eyebrow">Admin</span>
          <h1 className="mt-3 font-serif-display text-3xl font-semibold text-secondary">Quotes</h1>
        </div>
        <select
          data-testid="admin-quotes-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-full border border-border/70 bg-background px-4 py-2 text-xs font-semibold text-secondary"
        >
          <option value="">All statuses</option>
          <option value="submitted">Submitted</option>
          <option value="prepared">Prepared</option>
          <option value="approved">Approved</option>
          <option value="declined">Declined</option>
          <option value="new">Legacy: new</option>
          <option value="reviewed">Legacy: reviewed</option>
        </select>
      </div>

      {/* Mobile card list */}
      <div className="mt-8 space-y-3 sm:hidden" data-testid="admin-quotes-mobile-list">
        {loading && (
          <div className="rounded-2xl border border-border/70 bg-card p-5 text-center text-sm text-muted-foreground">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="rounded-2xl border border-border/70 bg-card p-6 text-center text-sm text-muted-foreground">No quotes.</div>
        )}
        {rows.map((q) => (
          <div key={q.id} data-testid={`admin-quote-card-${q.id}`} className="rounded-2xl border border-border/70 bg-card p-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="break-words font-medium text-secondary">{q.customer_name || "—"}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {q.created_at ? new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : ""}
                </div>
              </div>
              <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-secondary">
                {q.status}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {!q.customer_id && <span className="rounded-full bg-muted px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-muted-foreground">guest · WhatsApp only</span>}
              {q.customer_confirmed_at && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-emerald-800">✓ confirmed</span>}
            </div>
            <div className="mt-2 text-xs text-secondary">
              {q.event_type || "—"} {q.event_date || ""} · {q.package_snapshot?.name || "—"} · {q.guest_count || "—"} guests
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-secondary">
                {q.final_amount != null ? `₹${Number(q.final_amount).toLocaleString("en-IN")}` : "—"}
              </div>
              <Link
                data-testid={`admin-quote-open-m-${q.id}`}
                to={`/admin/quotes/${q.id}`}
                className="btn-ghost !py-2 !text-xs"
              >
                {q.customer_id ? "Edit" : "View"} →
              </Link>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 hidden overflow-hidden rounded-2xl border border-border/70 bg-card sm:block" data-testid="admin-quotes-table">
        <table className="w-full text-sm">
          <thead className="border-b border-border/70 bg-muted/50 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">Package</th>
              <th className="px-4 py-3">Guests</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Final ₹</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground" data-testid="admin-quotes-empty">No quotes.</td></tr>
            )}
            {rows.map((q) => (
              <tr key={q.id} data-testid={`admin-quote-row-${q.id}`} className="border-b border-border/60 last:border-none">
                <td className="px-4 py-3 text-xs text-muted-foreground">{q.created_at ? new Date(q.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" }) : ""}</td>
                <td className="px-4 py-3 font-medium text-secondary">
                  {q.customer_name || "—"}
                  {!q.customer_id && <span data-testid={`admin-quote-guest-badge-${q.id}`} className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-muted-foreground">guest · WhatsApp only</span>}
                  {q.customer_confirmed_at && <span data-testid={`admin-quote-confirmed-badge-${q.id}`} className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] text-emerald-800">✓ confirmed</span>}
                </td>
                <td className="px-4 py-3">{q.event_type || "—"} {q.event_date || ""}</td>
                <td className="px-4 py-3">{q.package_snapshot?.name || "—"}</td>
                <td className="px-4 py-3">{q.guest_count || "—"}</td>
                <td className="px-4 py-3">
                  <span className="rounded-full bg-muted px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-secondary">
                    {q.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">{q.final_amount != null ? `₹${Number(q.final_amount).toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <Link data-testid={`admin-quote-edit-${q.id}`} to={`/admin/quotes/${q.id}`} className="text-xs font-semibold uppercase tracking-[0.14em] text-primary hover:underline">
                    {q.customer_id ? "Edit" : "View"}
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
